import { useMemo, useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { geometryApi } from "../../api/blades";
import { bladeMesh, revolutionMesh } from "../../utils/bladeMesh";

import { useChartColors } from "../../chartTheme";

export default function Assembly3DView({ members = [], height = "700px" }) {
  const { BLADE_PALETTE, CASING_PALETTE } = useChartColors();
  const [viewsMap, setViewsMap] = useState({});

  useEffect(() => {
    const ids = [...new Set(members.map(m => m.blade_design?.id).filter(Boolean))];
    ids.forEach(id => {
      if (viewsMap[id]) return;
      geometryApi.bladeViews(id)
        .then(({ data }) => setViewsMap(prev => ({ ...prev, [id]: data })))
        .catch(() => {});
    });
  }, [members]); // eslint-disable-line react-hooks/exhaustive-deps

  const traces = useMemo(() => {
    const out = [];

    members.forEach((member) => {
      const idx = member.position % CASING_PALETTE.length;
      const casingColor = CASING_PALETTE[idx];
      const bladeColor  = BLADE_PALETTE[idx];
      const lines   = member.blade_design?.construction_lines || [];
      const zOff    = member.z_offset || 0;
      const nBlades = member.blade_design?.n_blades || 6;
      const views   = viewsMap[member.blade_design?.id];
      const dname   = member.blade_design?.name ?? "";
      const mz      = !!member.mirror_z;  // mirror about x-y plane: z → -z
      const mx      = !!member.mirror_x;  // mirror about y-z plane: x → -x

      // Hub / shroud as revolution mesh3d
      lines
        .filter(cl => cl.name === "HUB" || cl.name === "SHROUD")
        .forEach((cl) => {
          if (!cl.z_coords?.length || !cl.r_coords?.length) return;
          const zShifted = cl.z_coords.map(z => (mz ? -z : z) + zOff);
          out.push(revolutionMesh(
            cl.r_coords, zShifted,
            casingColor,
            `${dname} · ${cl.name}`,
            cl.name === "HUB" ? 0.4 : 0.2,
          ));
        });

      // All n_blades copies merged into ONE mesh3d trace via bladeMesh
      const bladeSurf = views?.surface_3d?.blade;
      if (bladeSurf) {
        const surf = {
          x: mx ? bladeSurf.x.map(row => row.map(v => -v)) : bladeSurf.x,
          y: bladeSurf.y,
          z: bladeSurf.z.map(row => row.map(v => (mz ? -v : v) + zOff)),
        };
        out.push(bladeMesh(surf, nBlades, bladeColor, dname, 0.90));
      }
    });

    return out;
  }, [members, viewsMap]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!members.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
        No blade rows — add members in the Blade Rows tab.
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      {traces.length === 0 && (
        <div className="flex items-center justify-center h-full min-h-[200px] text-gray-500 text-sm">
          No construction line data — define MECO lines in the blade editor first.
        </div>
      )}
      {traces.length > 0 && (
        <Plot
          data={traces}
          layout={{
            autosize: true,
            margin: { t: 24, r: 24, b: 24, l: 24 },
            scene: {
              xaxis: { title: "x [mm]", zeroline: false },
              yaxis: { title: "y [mm]", zeroline: false },
              zaxis: { title: "z [mm]", zeroline: false },
              aspectmode: "data",
              bgcolor: "#f9fafb",
            },
            paper_bgcolor: "transparent",
            font: { size: 11 },
            showlegend: true,
            legend: {
              orientation: "v", font: { size: 10 },
              bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1,
              x: 0, xanchor: "left", y: 1, yanchor: "top",
            },
          }}
          config={{
            displayModeBar: "hover",
            modeBarButtons: [["zoom3d", "pan3d", "orbitRotation", "resetCameraDefault3d", "toImage"]],
            scrollZoom: true, responsive: true, displaylogo: false,
          }}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler
        />
      )}
    </div>
  );
}
